// The iOS app's bridge (window.moodsNative + its MoodsNative plugin through Capacitor's injected `window.Capacitor`)
// without any app imports, so low-level modules (store/auth, lib/api via desktop-bridge) can use it. Everything else
// goes through lib/ios.ts. In a browser and in the desktop app `iosApp` is null and every helper here is a no-op.
import { NATIVE_PLUGIN, type MoodsNativeShell, type NativePushRegistration, type NativePushStatus, type NativeSecret } from '@moods/shared';

export const iosApp: MoodsNativeShell | null =
  typeof window !== 'undefined' && window.moodsNative?.platform === 'ios' ? window.moodsNative : null;

/** One call to the app's plugin; rejects outside the iOS app. */
export function iosCall<T = void>(method: string, options: object = {}): Promise<T> {
  const cap = typeof window !== 'undefined' ? window.Capacitor : undefined;
  if (!iosApp || !cap) return Promise.reject(new Error('not in the MOODS iOS app'));
  return cap.nativePromise<T>(NATIVE_PLUGIN, method, options);
}

/** A Keychain item of the app (null when it is missing or the call fails). */
export function getIosSecret(key: NativeSecret): Promise<string | null> {
  return iosCall<{ value?: string | null }>('getSecret', { key })
    .then(r => r.value ?? null)
    .catch(() => null);
}

/** Writes (or with null deletes) a Keychain item of the app. */
export function setIosSecret(key: NativeSecret, value: string | null): Promise<void> {
  return iosCall('setSecret', { key, value }).catch(() => undefined);
}

/** The app's notification permission (`prompt` when never asked or outside the app). */
export function iosPushStatus(): Promise<NativePushStatus> {
  return iosCall<{ status?: NativePushStatus }>('pushStatus')
    .then(r => r.status ?? 'prompt')
    .catch(() => 'prompt' as const);
}

/** Asks for permission when never asked (from a tap), then the APNs device token. Rejects when APNs gives none. */
export function iosPushRegister(): Promise<NativePushRegistration> {
  return iosCall<NativePushRegistration>('pushRegister');
}
